import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaHome,
  FaSignOutAlt,
  FaChevronDown,
  FaChevronUp,
  FaWhatsapp,
  FaInbox,
  FaFileExcel,
  FaCheckCircle,
  FaUsers,
  FaImage,
  FaChartBar,
  FaProjectDiagram,
  FaCode,
  FaBullhorn,
  FaBook,
  FaHeadset,
  FaCloud,
  FaShieldAlt,
  FaChrome,
  FaShopify,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { CLOUD_API_FEATURE_KEY, QR_SESSIONS_FEATURE_KEY, isFeatureAvailable } from "../utils/featureEntitlements";

const mainItems = [
  { icon: FaHome, label: "Dashboard", path: "/dashboard" },
  { icon: FaInbox, label: "Unibox", path: "/unibox" },
];

const automationItems = [
  { icon: FaProjectDiagram, label: "Flow Builder", path: "/flow-builder" },
  { icon: FaShopify, label: "Shopify", path: "/shopify" },
  { icon: FaBullhorn, label: "Campaigns", path: "/campaigns" },
];

const toolsSubItems = [
  { icon: FaFileExcel, label: "Send via Excel", path: "/send-via-excel" },
  { icon: FaCheckCircle, label: "Number Verification", path: "/number-verification" },
  { icon: FaUsers, label: "Group Member Export", path: "/group-members-export" },
  { icon: FaImage, label: "Image/File to URL", path: "/image-to-url" },
  { icon: FaCode, label: "WhatsApp Widget", path: "/whatsapp-widget" },
];

export default function Sidebar({
  isCollapsed, isHovered, toggleSidebar,
  expandFully, onHover, onLeave,
  isMobile, isMobileOpen, closeMobileSidebar
}) {
  const [showTools, setShowTools] = useState(false);
  const isSidebarOpen = !isCollapsed || isHovered;
  const { logout, role, user } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const canUseQrSessions = isFeatureAvailable(subscription, QR_SESSIONS_FEATURE_KEY, role);
  const canUseCloudApi = isFeatureAvailable(subscription, CLOUD_API_FEATURE_KEY, role);
  const visibleMainItems = [
    mainItems[0],
    ...(canUseQrSessions ? [{ icon: FaWhatsapp, label: "WhatsApp Phones", path: "/whatsapp-phones" }] : []),
    ...(canUseCloudApi ? [{ icon: FaCloud, label: "Cloud API", path: "/cloud-sessions" }] : []),
    { icon: FaChrome, label: "Connect Browser", path: "/browser/sessions" },
    ...mainItems.slice(1),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U";

  const isActive = (path) => {
    if (path.includes("#")) {
      return location.pathname + location.hash === path;
    }
    return location.pathname === path;
  };

  const handleNavClick = () => {
    if (isMobile) closeMobileSidebar();
    else if (isCollapsed) expandFully();
  };

  const sidebarClasses = [
    "sidebar",
    !isMobile && !isSidebarOpen ? "collapsed" : "",
    isMobile && isMobileOpen ? "mobile-open" : "",
  ].filter(Boolean).join(" ");

  const renderItem = (item, key) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        key={key}
        className={active ? "sidebar-item active" : "sidebar-item"}
        data-tooltip={!isSidebarOpen ? item.label : undefined}
        onClick={handleNavClick}
      >
        <span className="sidebar-icon"><Icon /></span>
        {isSidebarOpen && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <>
      <div
        className={sidebarClasses}
        style={isMobile ? {
          transform: isMobileOpen ? "translateX(0)" : "translateX(-100%)",
          zIndex: 1041,
        } : undefined}
        onMouseEnter={!isMobile ? onHover : undefined}
        onMouseLeave={!isMobile ? onLeave : undefined}
      >
        {/* Logo */}
        <Link to="/dashboard" className="flex h-16 items-center justify-center border-b border-sidebar-border px-5">
          {isSidebarOpen ? (
            <img
              src="/walytic-logo-white.png"
              alt="Walytic"
              className="h-7 w-auto object-contain"
            />
          ) : (
            <img
              src="/walytic-favicon.png"
              alt="Walytic"
              className="h-7 w-7 object-contain"
            />
          )}
        </Link>

        {/* Main nav */}
        <nav className="flex-1 py-2">
          {isSidebarOpen && <p className="sidebar-section-label">Workspace</p>}
          {visibleMainItems.map((item, i) => renderItem(item, `main-${i}`))}

          {isSidebarOpen ? <p className="sidebar-section-label">Automation</p> : <div className="divider-sidebar" />}
          {automationItems.map((item, i) => renderItem(item, `auto-${i}`))}

          {/* Tools dropdown */}
          {isSidebarOpen ? <p className="sidebar-section-label">Operations</p> : <div className="divider-sidebar" />}
          <button
            onClick={() => {
              setShowTools(!showTools);
              if (!isSidebarOpen && isCollapsed) expandFully();
            }}
            className={`sidebar-item w-full text-left ${showTools ? "active" : ""}`}
            data-tooltip={!isSidebarOpen ? "Tools" : undefined}
          >
            <span className="sidebar-icon"><FaChevronDown /></span>
            {isSidebarOpen && (
              <>
                <span className="flex-1">Tools</span>
                {showTools ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </>
            )}
          </button>

          {showTools && isSidebarOpen && (
            <div className="ml-4">
              {toolsSubItems.map((tool, i) => renderItem(tool, `tool-${i}`))}
            </div>
          )}

          {/* Resources */}
          {renderItem({ icon: FaChartBar, label: "Reports", path: "/reports" }, "bottom-reports")}
          {isSidebarOpen ? <p className="sidebar-section-label">Resources</p> : <div className="divider-sidebar" />}
          {renderItem({ icon: FaBook, label: "User Guide", path: "/guide" }, "bottom-guide")}

          {/* Support — re-opens the floating WhatsApp widget after the user dismissed it via X */}
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined" && window.WalyticWidget && typeof window.WalyticWidget.show === "function") {
                window.WalyticWidget.show();
              } else {
                window.open("https://wa.me/918926314377?text=Hi%2C%20I%20need%20help%20with%20Walytic", "_blank", "noopener,noreferrer");
              }
              if (isMobile) closeMobileSidebar();
            }}
            className="sidebar-item w-full text-left"
            data-tooltip={!isSidebarOpen ? "Support" : undefined}
          >
            <span className="sidebar-icon"><FaHeadset /></span>
            {isSidebarOpen && <span>Support</span>}
          </button>
        </nav>

        {/* Account */}
        <div className="p-3 border-t border-sidebar-border">
          <button
            onClick={() => navigate("/settings")}
            className="sidebar-item w-full text-left"
            data-tooltip={!isSidebarOpen ? "Profile & Settings" : undefined}
          >
            <span className="sidebar-icon">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name || user.email} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                  {initials}
                </span>
              )}
            </span>
            {isSidebarOpen && (
              <span className="min-w-0">
                <span className="block truncate">{user?.name || "Profile"}</span>
                <span className="block truncate text-[10px] text-sidebar-muted">{user?.email || "Settings"}</span>
              </span>
            )}
          </button>

          {role === "admin" && (
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="sidebar-item w-full text-left"
              data-tooltip={!isSidebarOpen ? "Admin" : undefined}
            >
              <span className="sidebar-icon"><FaShieldAlt /></span>
              {isSidebarOpen && <span>Admin Dashboard</span>}
            </button>
          )}

          <button
            onClick={handleLogout}
            className="sidebar-item w-full text-left hover:!bg-red-50 hover:!text-red-500 dark:hover:!bg-red-950/40 dark:hover:!text-red-400"
            data-tooltip={!isSidebarOpen ? "Logout" : undefined}
          >
            <span className="sidebar-icon"><FaSignOutAlt /></span>
            {isSidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobile && isMobileOpen && (
        <div className="sidebar-overlay" onClick={closeMobileSidebar} />
      )}
    </>
  );
}
